import type { PushApi } from "@majitap/ui";
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";

/**
 * The signed-in person's inbox (P11-02): the unread count, polled every minute and on focus, and
 * the newest notifications, fetched when the bell opens.
 */
export const unreadQuery = queryOptions({
  queryKey: ["notifications", "unread"],
  refetchInterval: 60_000,
  staleTime: 15_000,
  queryFn: () => ok(api.GET("/v1/admin/notifications/unread-count")),
});

/**
 * The newest few, checked every 20 seconds (also while the tab is in the background) so new
 * ones pop up as toasts with a chime.
 */
export const arrivalsQuery = queryOptions({
  queryKey: ["notifications", "arrivals"],
  refetchInterval: 20_000,
  refetchIntervalInBackground: true,
  queryFn: () => ok(api.GET("/v1/admin/notifications", { params: { query: { limit: 10 } } })),
});

export const inboxQuery = queryOptions({
  queryKey: ["notifications", "list"],
  queryFn: () => ok(api.GET("/v1/admin/notifications", { params: { query: { limit: 20 } } })),
});

export const markRead = (id: string) => ok(api.POST("/v1/admin/notifications/{id}/read", { params: { path: { id } } }));

export const markAllRead = () => ok(api.POST("/v1/admin/notifications/read-all"));

/** After a notification is read: the count and the list both change. */
export const refreshNotifications = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: ["notifications"] });

/** Push on this device (P11-05), for `usePush` and `forgetPush`. */
export const pushApi: PushApi = {
  publicKey: async () => (await ok(api.GET("/v1/admin/notifications/push/public-key"))).public_key,
  subscribe: (body) => ok(api.POST("/v1/admin/notifications/push-subscriptions", { body })),
  unsubscribe: (endpoint) =>
    ok(api.DELETE("/v1/admin/notifications/push-subscriptions", { params: { query: { endpoint } } })),
};

/** What reaches me, per topic and channel (P11-07). */
export const preferencesQuery = queryOptions({
  queryKey: ["notifications", "preferences"],
  queryFn: () => ok(api.GET("/v1/admin/notifications/preferences")),
});

export const setPreference = (topic: string, channel: "sms" | "webpush" | "fcm", enabled: boolean) =>
  ok(api.PUT("/v1/admin/notifications/preferences", { body: { topic, channel, enabled } }));
